define('presentation/login/viewModels/payBalanceViewModel',[
    'businessServices/authentication/authenticationManager',
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/authentication/sessionWorkflowFlags',
    'businessServices/converters/currencyFormatter',
    'businessServices/login/accountStatusEvaluator',
    'common/promises/promiseFactory',
    'constants/billingMethodConstants',
    'constants/creditCardResponseConstants',
    'plugins/router',
    'presentation/common/paymentMethod/viewModels/addPaymentMethodViewModel',
    'presentation/login/facades/payBalanceFacade',
    'presentation/login/validators/payBalanceValidator',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const CurrencyFormatterConstructor = require('businessServices/converters/currencyFormatter');
        const _currencyFormatter = new CurrencyFormatterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _i18n = require('i18next');
        const _accountStatusEvaluator = require('businessServices/login/accountStatusEvaluator');
        const _authenticationManager = require('businessServices/authentication/authenticationManager');
        const _billingMethodConstants = require('constants/billingMethodConstants');
        const _creditCardResponse = require('constants/creditCardResponseConstants');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('plugins/router');
        const _sessionAccountInfo = require('businessServices/authentication/sessionAccountInfo');
        const _sessionWorkflowFlags = require('businessServices/authentication/sessionWorkflowFlags');

        let _addPaymentMethodViewModel = null;
        let _facade = null;
        let _validator = null;
        let _availablePaymentMethods = null;

        const NEW_PAYMENT_METHOD =  {
            paymentMethodId: "new",
            description: _i18n.t("payBalance:newCard")
        };

        const _getDeclinedInformation = () => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.getDeclinedInformation()
                    .fail(deferredObject.reject)
                    .done((declinedInformation) => {
                        self.declinedInformation = declinedInformation;
                        self.billingMethod(declinedInformation.billingMethod || _billingMethodConstants.CreditCard);
                        if (declinedInformation.autoCloseDate === '') {
                            self.isCreditCardDeclined(false);
                        } else {
                            self.isCreditCardDeclined(true);
                        }
                        if (self.isBilledByCreditCard()) {
                            _facade.getAllPaymentMethods()
                                .fail(deferredObject.reject)
                                .done((paymentMethods) => {
                                    _availablePaymentMethods = paymentMethods.filter((paymentMethod) => {
                                        return paymentMethod.isExpired() === false;
                                    });
                                    _buildPaymentMethodsDropDownOptions()
                                        .fail(deferredObject.reject)
                                        .done((paymentMethodDropDownOptions) => {
                                            self.paymentMethodDropDownOptions(paymentMethodDropDownOptions);
                                            _setSelectedPaymentMethod();
                                            deferredObject.resolve();
                                        });
                                });
                        } else {
                            deferredObject.resolve();
                        }
                    });
            });
        };

        const _buildPaymentMethodsDropDownOptions = () => {
            return _promiseFactory.defer((deferredObject) => {
                let paymentMethodDropDownOptions = _availablePaymentMethods.map((paymentMethod) => {
                    return {
                        paymentMethodId: paymentMethod.paymentMethodId,
                        cardHolderName: paymentMethod.cardHolderName,
                        cardType: paymentMethod.cardType,
                        expirationDate: paymentMethod.expirationDate,
                        country: paymentMethod.country,
                        postalCode: paymentMethod.postalCode,
                        description: paymentMethod.description
                    };
                });

                _facade.getAddPaymentMethodAllowed()
                    .fail(deferredObject.reject)
                    .done((addPaymentMethodAllowed) => {
                        if (addPaymentMethodAllowed) {
                            paymentMethodDropDownOptions.push(NEW_PAYMENT_METHOD);
                        }
                        deferredObject.resolve(paymentMethodDropDownOptions);
                    });
            });
        };

        const _setSelectedPaymentMethod = () => {
            let declinedPaymentMethod = _availablePaymentMethods.find((paymentMethod) => {
                return self.declinedInformation.paymentMethodId === paymentMethod.paymentMethodId;
            });

            let defaultPaymentMethod = null;
            if (declinedPaymentMethod === undefined) {
                defaultPaymentMethod = _availablePaymentMethods.find((paymentMethod) => {
                    return paymentMethod.isDefault();
                });
            }

            let selectedPaymentMethod = declinedPaymentMethod || defaultPaymentMethod;
            if (selectedPaymentMethod === undefined) {
                self.selectedPaymentMethod(NEW_PAYMENT_METHOD);
            } else {
                self.selectedPaymentMethod(selectedPaymentMethod);
            }
        };

        const _addPaymentMethodAndMakePayment = () => {
            return _promiseFactory.defer((deferredObject) => {
                let paymentMethod = _addPaymentMethodViewModel.paymentMethod();
                _facade.addPaymentMethodAndMakePayment(paymentMethod, self.declinedInformation.pastDueBalance)
                    .fail(deferredObject.reject)
                    .done((addPaymentMethodAndMakePaymentResponse) => {
                        if (_validatePaymentProcessing(addPaymentMethodAndMakePaymentResponse.status)) {
                            let paymentMethodPresentationObject = _facade.buildPaymentMethodPresentationObject(addPaymentMethodAndMakePaymentResponse.paymentMethod);
                            self.selectedPaymentMethod(paymentMethodPresentationObject);
                            deferredObject.resolve(true);
                        } else {
                            deferredObject.resolve(false);
                        }
                    });
            });
        };

        const _makePayment = (paymentMethodId) => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.makePayment(paymentMethodId, self.declinedInformation.pastDueBalance.toString())
                    .fail(deferredObject.reject)
                    .done((paymentResults) => {
                        if (_validatePaymentProcessing(paymentResults.status)) {
                            let selectedPaymentMethod = self.selectedPaymentMethod();
                            self.selectedPaymentMethod(selectedPaymentMethod);
                            deferredObject.resolve(true);
                        } else {
                            deferredObject.resolve(false);
                        }
                    });
            });
        };

        const _validatePaymentProcessing = (paymentStatus) => {
            switch (paymentStatus) {
                case _creditCardResponse.success:
                case _creditCardResponse.approved:
                    self.paymentFailureTitle('');
                    self.paymentFailureMessage('');
                    return true;
                case _creditCardResponse.declined:
                    self.paymentFailureTitle(_i18n.t('payBalance:declinedTitle'));
                    self.paymentFailureMessage(_i18n.t('payBalance:declinedMessage'));
                    return false;
                case _creditCardResponse.duplicateCard:
                case _creditCardResponse.invalidCard:
                case _creditCardResponse.invalidConnection:
                    self.paymentFailureTitle(_i18n.t('payBalance:genericFailureTitle'));
                    self.paymentFailureMessage(_i18n.t('payBalance:genericFailureMessage'));
                    return false;
                default:
                    throw new Error(paymentStatus);
            }
        };

        self.isCompositionComplete = ko.observable(false);
        self.isSubmitInProgress = ko.observable(false);
        self.isScrollActive = ko.observable(false);
        self.addPaymentMethodViewModel = ko.observable();
        self.isBalancePaid = ko.observable(false);
        self.balancePaidAmount = ko.observable('');
        self.customerAgreesToPay = ko.observable(false);
        self.paymentMethodDropDownOptions = ko.observableArray([]);
        self.selectedPaymentMethod = ko.observable('');
        self.isCreditCardDeclined = ko.observable(true);
        self.billingMethod = ko.observable(_billingMethodConstants.CreditCard);
        self.showValidation = ko.observable(false);
        self.showPaymentFailureMessage = ko.observable(false);
        self.paymentFailureTitle = ko.observable('');
        self.paymentFailureMessage = ko.observable('');

        self.shouldDisplayAgentBypassButton = ko.pureComputed(() => {
            return _sessionAccountInfo.isImpersonating();
        });

        self.isBilledByCreditCard = ko.pureComputed(() => self.billingMethod() === _billingMethodConstants.CreditCard);

        self.shouldContactUsToPayBalance = ko.pureComputed(() => self.billingMethod() !== _billingMethodConstants.CreditCard);

        self.isNewPaymentMethod = ko.pureComputed(() => {
            if (self.isBilledByCreditCard() === false) {
                return false;
            }
            return self.selectedPaymentMethod().paymentMethodId === NEW_PAYMENT_METHOD.paymentMethodId;
        });

        self.headerTitle = ko.pureComputed(() => {
            return _i18n.t("payBalance:warningTitle", {
                pastDueBalance: _currencyFormatter.formatUSDForDisplay(self.declinedInformation.pastDueBalance)
            });
        });

        self.headerMessage = ko.pureComputed(() => {
            if (self.declinedInformation.autoCloseDate) {
                return _i18n.t("payBalance:warningMessageAutoClose", {
                    autoCloseDate: self.declinedInformation.autoCloseDate
                });
            }
            if (self.isBilledByCreditCard() === false) {
                return _i18n.t("payBalance:warningMessageNoPaymentMethod");
            }
            return _i18n.t("payBalance:warningMessage");
        });

        self.customerAgreesToPayAcknowledgement = ko.pureComputed(() => {
            return _i18n.t("payBalance:customerAgreesToPayAcknowledgement", {
                pastDueBalance: _currencyFormatter.formatUSDForDisplay(self.declinedInformation.pastDueBalance)
            });
        });

        self.customerAgreesToPayIsValid = ko.pureComputed(() => {
            if (self.showValidation()) {
                return self.customerAgreesToPay();
            }
            return true;
        });

        self.balancePaidMessage = ko.pureComputed(() => {
            return _i18n.t("payBalance:balancePaidMessage", {
                paymentAmount: self.balancePaidAmount()
            });
        });

        self.onContinue = () => {
            _router.navigate(_navigationConfiguration.loginContinuationPrompt);
        };

        self.onSignOut = () => {
            _authenticationManager.logout()
                .done(() => {
                    _router.navigate(_navigationConfiguration.loginPageUrl);
                });
        };

        self.onCancel = () => {
            if (self.isSubmitInProgress()) {
                return;
            }

            _sessionWorkflowFlags.hasBypassedPastDueBalance(true);
            _router.navigate(_navigationConfiguration.loginContinuationPrompt);
        };

        self.onAgentBypass = () => {
            if (self.isSubmitInProgress()) {
                return;
            }

            _sessionWorkflowFlags.hasBypassedPastDueBalance(true);
            _router.navigate(_navigationConfiguration.loginLandingPageUrl);
        };

        self.payNow = () => {
            self.showValidation(false);
            self.showPaymentFailureMessage(false);
            return _promiseFactory.deferWithMinimumWait((deferredObject) => {
                let payBalancePromiseFactory = new PromiseFactoryConstructor();
                let addPaymentMethodIsValid = false;
                let payBalanceIsValid = false;

                if (self.isNewPaymentMethod()) {
                    payBalancePromiseFactory.defer((newPaymentDeferred) => {
                        self.addPaymentMethodViewModel().validate()
                            .fail(newPaymentDeferred.reject)
                            .done((isValid) => {
                                addPaymentMethodIsValid = isValid;
                                newPaymentDeferred.resolve();
                            });
                    });
                } else {
                    addPaymentMethodIsValid = true;
                }

                payBalancePromiseFactory.defer((validatorDeferred) => {
                    _validator.validate()
                        .fail(validatorDeferred.reject)
                        .done((isValid) => {
                            payBalanceIsValid = isValid;
                            validatorDeferred.resolve();
                        });
                });

                payBalancePromiseFactory.wait()
                    .fail(deferredObject.reject)
                    .done(() => {
                        if (addPaymentMethodIsValid && payBalanceIsValid) {
                            let promise;
                            if (self.isNewPaymentMethod()) {
                                promise = _addPaymentMethodAndMakePayment();
                            } else {
                                promise = _makePayment(self.selectedPaymentMethod().paymentMethodId);
                            }
                            promise
                                .fail(deferredObject.reject)
                                .done((paymentProcessed) => {
                                    if (paymentProcessed) {
                                        self.balancePaidAmount(_currencyFormatter.formatUSDForDisplay(self.declinedInformation.pastDueBalance));
                                        _accountStatusEvaluator.refresh();
                                        self.isBalancePaid(true);
                                        deferredObject.resolve();
                                    } else {
                                        self.showPaymentFailureMessage(true);
                                        deferredObject.resolve();
                                    }
                                });
                        } else {
                            self.showValidation(true);
                            deferredObject.resolve();
                        }
                    });
            });
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/login/facades/payBalanceFacade');
            _facade = new FacadeConstructor();
            _facade.init();

            const ValidatorConstructor = require('presentation/login/validators/payBalanceValidator');
            _validator = new ValidatorConstructor();

            const AddPaymentMethodViewModel = require('presentation/common/paymentMethod/viewModels/addPaymentMethodViewModel');
            _addPaymentMethodViewModel = new AddPaymentMethodViewModel();
            self.addPaymentMethodViewModel(_addPaymentMethodViewModel);

            return _initialize();
        };

        const _initialize = () => {
            _getDeclinedInformation();

            _validator.registerViewModel(self);

            return _promiseFactory.wait();
        };
    };
});
